/**
 * Helper for tRPC mutation wrappers that need to surface the
 * `CHANGE_REQUEST_REQUIRED` cause to the user via the confirm modal and
 * replay the mutation with `_confirmChangeRequest: true`.
 *
 * Usage from a `useUpdate*TRPC` hook:
 *
 *   const { requestConfirmation } = useChangeRequestConfirm();
 *
 *   const updateRisk = (input) =>
 *     withChangeRequestConfirm({
 *       input,
 *       run: (variables) => mutation.mutateAsync(variables),
 *       requestConfirmation,
 *     });
 *
 * The helper:
 *   1. invokes `run(input)`;
 *   2. on `CHANGE_REQUEST_REQUIRED` error, calls `requestConfirmation()`;
 *   3. if the user confirms, replays `run({ ...input, _confirmChangeRequest, _requesterComment })`;
 *   4. if the user cancels, re-throws the original error so the form
 *      layer can render it (currently `mapFormError` returns
 *      `{kind: 'change-request-required'}` for cancellation — FormContext
 *      treats that as a no-op submit).
 *
 * Spec reference: RSP-1528 Phase 6b.
 */
import type { ChangeRequestConfirmContextValue } from 'src/providers/change-request-confirm-context';

import { tryExtractChangeRequestRequired } from '@/utils/formError';

/**
 * True when a confirmed-replay result shows the change APPLIED DIRECTLY rather
 * than raising a CR. The gate is re-evaluated server-side on the confirmed
 * retry, so if the approval levels were removed between the initial
 * `CHANGE_REQUEST_REQUIRED` and the retry, the mutation applies and returns
 * `ApprovalGatedResult { status: 'applied' }`. Only that case must NOT mark a
 * CR as created (otherwise FormContext shows a spurious "submitted for
 * approval"). Any other shape — the CR statuses, or a bulk result — is treated
 * as "raised a CR" (the common confirmed path). `'status' in result` narrows
 * without a cast.
 */
const replayAppliedDirectly = (result: unknown): boolean => {
  if (typeof result !== 'object' || result === null || !('status' in result)) {
    return false;
  }
  const { status } = result;

  return status === 'applied';
};

export const withChangeRequestConfirm = async <TInput, TOutput>({
  input,
  run,
  confirm,
}: {
  input: TInput;
  run: (variables: TInput) => Promise<TOutput>;
  /**
   * The change-request-confirm context — supplies the modal prompt AND the
   * `markChangeRequestCreated` signal FormContext reads to show the right
   * post-submit toast + navigation (RSP-6099 I/K).
   */
  confirm: Pick<
    ChangeRequestConfirmContextValue,
    'markChangeRequestCreated' | 'requestConfirmation'
  >;
}): Promise<TOutput> => {
  try {
    return await run(input);
  } catch (error) {
    if (!tryExtractChangeRequestRequired(error)) {
      throw error;
    }

    const confirmed = await confirm.requestConfirmation();
    if (!confirmed) {
      throw error;
    }

    // Spread the confirm flag fields onto the input. The wrapper input
    // types track the legacy GraphQL mutation shape (which doesn't
    // include `_confirmChangeRequest` / `_requesterComment`) but the
    // underlying tRPC procedure's Zod schema accepts both — runtime is
    // fine, TS needs the unknown round-trip.
    const retryInput = {
      ...input,
      _confirmChangeRequest: true,
      _requesterComment: confirmed.requesterComment || null,
    } as unknown as TInput;

    const result = await run(retryInput);
    // The confirmed replay raised (or amended) a change request — flag it so
    // FormContext shows "submitted for approval" instead of a false
    // "updated successfully", and keeps the user on the entity (RSP-6099 I/K).
    // Skip the flag if the gate vanished between attempts and the retry
    // applied directly, so we don't mislabel a real apply.
    if (!replayAppliedDirectly(result)) {
      confirm.markChangeRequestCreated();
    }

    return result;
  }
};
